import React, { useCallback, useEffect, useMemo, useState, FormEvent } from 'react';
import {
  billingApi,
  BillingSettings,
  CompanyDto,
  CompanyUserDto,
  InvoiceRecord,
  tenancyApi,
} from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { Link, useLocation, useNavigate, useParams } from '../../router/Router';
import { Badge, formatDateTime } from '../../components/ui/Badge';
import { Dialog } from '../../components/ui/Dialog';
import { IconBuilding, IconPlus, IconSync } from '../../components/ui/Icons';
import { PaginationBar } from '../../components/ui/PaginationBar';
import {
  useDebouncedValue,
  usePaginatedList,
  useServerPaginatedQuery,
} from '../../hooks/usePagination';

export function AdminCompaniesPage() {
  const { companyId } = useParams<{ companyId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  const { pushToast } = useToast();

  // Companies DataTable State (Server-Paginated + 2-Page Lookahead Prefetch)
  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedSearch = useDebouncedValue(searchQuery, 250);
  const trimmedSearch = debouncedSearch.trim() || undefined;
  const companiesQuery = useServerPaginatedQuery<CompanyDto>({
    queryKey: `admin-companies:${trimmedSearch ?? ''}`,
    defaultPageSize: 10,
    enabled: !companyId,
    fetchPage: (p, size) =>
      tenancyApi.getCompaniesDataTable({
        page: p,
        page_size: size,
        search: trimmedSearch,
      }),
  });
  const isSearchingCompanies =
    companiesQuery.loading || searchQuery.trim() !== debouncedSearch.trim();

  // Create Company Modal State
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [newCompanyName, setNewCompanyName] = useState<string>('');
  const [newProcoreId, setNewProcoreId] = useState<string>('');
  const [creatingCompany, setCreatingCompany] = useState<boolean>(false);

  // Selected Company Detail State (`/admin/companies/:companyId/*`)
  const [selectedCompany, setSelectedCompany] = useState<CompanyDto | null>(null);
  const [companyUsers, setCompanyUsers] = useState<CompanyUserDto[]>([]);
  const [companyUserSearch, setCompanyUserSearch] = useState<string>('');
  const [billingSettings, setBillingSettings] = useState<BillingSettings | null>(null);
  const [companyInvoices, setCompanyInvoices] = useState<InvoiceRecord[]>([]);
  const [loadingDetail, setLoadingDetail] = useState<boolean>(false);

  const filteredCompanyUsers = useMemo(() => {
    const q = companyUserSearch.trim().toLowerCase();
    if (!q) return companyUsers;
    return companyUsers.filter(
      ({ user: u }) =>
        u.full_name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.id.toLowerCase().includes(q),
    );
  }, [companyUsers, companyUserSearch]);

  const companyUsersPagination = usePaginatedList(filteredCompanyUsers, 10);
  const companyInvoicesPagination = usePaginatedList(companyInvoices, 10);

  // Billing Settings Form State
  const [enableBillingDraft, setEnableBillingDraft] = useState<boolean>(true);
  const [licensePriceDraft, setLicensePriceDraft] = useState<string>('199.00');
  const [packagePriceDraft, setPackagePriceDraft] = useState<string>('999.00');
  const [trialDaysDraft, setTrialDaysDraft] = useState<number>(14);
  const [zohoIdDraft, setZohoIdDraft] = useState<string>('');
  const [stripeIdDraft, setStripeIdDraft] = useState<string>('');
  const [cardExistsDraft, setCardExistsDraft] = useState<boolean>(false);
  const [savingBilling, setSavingBilling] = useState<boolean>(false);

  const detailTab: 'users' | 'settings' | 'invoices' = location.pathname.endsWith('/settings')
    ? 'settings'
    : location.pathname.endsWith('/invoices')
      ? 'invoices'
      : 'users';

  useEffect(() => {
    if (companiesQuery.error) {
      pushToast({
        tone: 'error',
        title: 'Failed to load companies table',
        description: companiesQuery.error,
      });
    }
  }, [companiesQuery.error, pushToast]);

  const loadCompanyDetail = useCallback(
    async (targetCompanyId: string) => {
      setLoadingDetail(true);
      try {
        const [comp, usersRes, billingRes, invoicesRes] = await Promise.all([
          tenancyApi.getCompany(targetCompanyId).catch(() => null),
          tenancyApi.getCompanyUsers(targetCompanyId).catch(() => []),
          billingApi.getBillingSettings(targetCompanyId).catch(() => []),
          billingApi.getCompanyInvoicesAdmin(targetCompanyId).catch(() => []),
        ]);
        setSelectedCompany(comp);
        setCompanyUsers(Array.isArray(usersRes) ? usersRes : []);
        const bs = Array.isArray(billingRes) && billingRes.length > 0 ? billingRes[0] : null;
        setBillingSettings(bs);
        if (bs) {
          setEnableBillingDraft(bs.enable_billing);
          setLicensePriceDraft(bs.license_price);
          setPackagePriceDraft(bs.package_price);
          setTrialDaysDraft(bs.trial_days);
          setZohoIdDraft(bs.zoho_customer_id || '');
          setStripeIdDraft(bs.stripe_customer_id || '');
          setCardExistsDraft(bs.is_credit_card_exist);
        }
        setCompanyInvoices(Array.isArray(invoicesRes) ? invoicesRes : []);
      } finally {
        setLoadingDetail(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (companyId) {
      void loadCompanyDetail(companyId);
    }
  }, [companyId, loadCompanyDetail]);

  const handleCreateCompany = async (e: FormEvent) => {
    e.preventDefault();
    const procoreId = Number(newProcoreId);
    if (!newCompanyName.trim() || !Number.isFinite(procoreId) || procoreId <= 0) return;
    setCreatingCompany(true);
    try {
      const created = await tenancyApi.createCompany({
        name: newCompanyName.trim(),
        procore_company_id: procoreId,
      });
      await companiesQuery.reload();
      setCreateOpen(false);
      setNewCompanyName('');
      setNewProcoreId('');
      pushToast({
        tone: 'success',
        title: 'Company created',
        description: `${created.name} (#${created.procore_company_id}) registered.`,
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Could not create company',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setCreatingCompany(false);
    }
  };

  const handleSaveCompanyBillingSettings = async (e: FormEvent) => {
    e.preventDefault();
    if (!companyId) return;
    setSavingBilling(true);
    try {
      const updated = await billingApi.updateBillingSettings({
        company_id: companyId,
        enable_billing: enableBillingDraft,
        license_price: licensePriceDraft.trim(),
        package_price: packagePriceDraft.trim(),
        trial_days: Number(trialDaysDraft),
        zoho_customer_id: zohoIdDraft.trim() || undefined,
        stripe_customer_id: stripeIdDraft.trim() || undefined,
        is_credit_card_exist: cardExistsDraft,
      });
      setBillingSettings(updated);
      pushToast({
        tone: 'success',
        title: 'Company billing settings updated',
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Failed to update billing settings',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setSavingBilling(false);
    }
  };

  const handleMarkInvoicePaid = async (invoice: InvoiceRecord) => {
    try {
      const updated = await billingApi.markInvoiceAsPaid(invoice.id);
      setCompanyInvoices((prev) => prev.map((inv) => (inv.id === updated.id ? updated : inv)));
      pushToast({
        tone: 'success',
        title: `Invoice ${updated.invoice_number} marked as paid`,
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Could not mark invoice as paid',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    }
  };

  const handleLoginAsUser = async (userId: string, label: string) => {
    try {
      const res = await tenancyApi.loginAs({ user_id: userId, ttl_secs: 3600 });
      await startImpersonation(res.token, `${res.user.full_name} (${res.user.email})`);
      pushToast({
        tone: 'success',
        title: `Impersonating ${label}`,
        description: `Expires ${formatDateTime(res.expires_at)}`,
      });
      navigate('/projects');
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Login-As impersonation failed',
        description: err instanceof Error ? err.message : 'User may have disabled support access',
      });
    }
  };

  const filteredCompanies = companiesQuery.data;

  // --------------------------------------------------------------------------
  // Render Company Detail Sub-View (`/admin/companies/:companyId/*`)
  // --------------------------------------------------------------------------
  if (companyId) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-2xs">
          <div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Link to="/admin/companies" className="hover:text-indigo-600 font-medium">
                ← All Companies
              </Link>
              <span>/</span>
              <span className="font-mono">{companyId}</span>
            </div>
            <h1 className="mt-1 text-2xl font-bold text-slate-900">
              {selectedCompany?.name || 'Company Detail'}
            </h1>
            {selectedCompany && (
              <p className="text-xs text-slate-500 font-mono">
                Procore Company #{selectedCompany.procore_company_id} · Created{' '}
                {formatDateTime(selectedCompany.created_at)}
              </p>
            )}
          </div>

          <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
            <Link
              to={`/admin/companies/${companyId}/users`}
              className={`rounded-lg px-3.5 py-1.5 text-xs font-semibold ${
                detailTab === 'users' ? 'bg-indigo-600 text-white' : 'text-slate-600'
              }`}
            >
              Users ({companyUsers.length})
            </Link>
            <Link
              to={`/admin/companies/${companyId}/settings`}
              className={`rounded-lg px-3.5 py-1.5 text-xs font-semibold ${
                detailTab === 'settings' ? 'bg-indigo-600 text-white' : 'text-slate-600'
              }`}
            >
              Billing &amp; Trial Settings
            </Link>
            <Link
              to={`/admin/companies/${companyId}/invoices`}
              className={`rounded-lg px-3.5 py-1.5 text-xs font-semibold ${
                detailTab === 'invoices' ? 'bg-indigo-600 text-white' : 'text-slate-600'
              }`}
            >
              Invoices ({companyInvoices.length})
            </Link>
          </div>
        </div>

        {loadingDetail ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center text-sm text-slate-500">
            Loading company records…
          </div>
        ) : detailTab === 'users' ? (
          <div className="rounded-2xl border border-slate-200 bg-white shadow-2xs overflow-hidden">
            <div className="border-b border-slate-200 p-4">
              <input
                type="search"
                value={companyUserSearch}
                onChange={(e) => setCompanyUserSearch(e.target.value)}
                placeholder="Search users in this company by name, email, or UUID…"
                className="w-full rounded-xl border border-slate-300 px-3.5 py-2 text-sm"
              />
            </div>
            <div className="table-scroll-container">
              <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
                <thead className="bg-slate-50 text-slate-600 uppercase font-semibold">
                  <tr>
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3">OAuth Credentials</th>
                    <th className="px-4 py-3">Billing Role</th>
                    <th className="px-4 py-3">Login-As Consent</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {companyUsersPagination.pageItems.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                        No users found for this company.
                      </td>
                    </tr>
                  ) : (
                    companyUsersPagination.pageItems.map(({ user: u, has_billing_permission }) => (
                      <tr key={u.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-slate-900">{u.full_name}</div>
                          <div className="text-slate-500">{u.email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {u.procore_oauth?.connected && <Badge variant="purple">Procore</Badge>}
                            {u.smartsheet_oauth?.connected && (
                              <Badge variant="info">Smartsheet</Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {has_billing_permission ? (
                            <Badge variant="purple">Billing Permission</Badge>
                          ) : (
                            <span className="text-slate-400">Member</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={u.is_login_as_allowed ? 'success' : 'warning'}>
                            {u.is_login_as_allowed ? 'Allowed' : 'Disabled'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            disabled={!u.is_login_as_allowed}
                            onClick={() => handleLoginAsUser(u.id, u.email)}
                            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-40"
                          >
                            Login As User →
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <PaginationBar pagination={companyUsersPagination} itemLabel="users" compact />
          </div>
        ) : detailTab === 'settings' ? (
          <form
            onSubmit={handleSaveCompanyBillingSettings}
            className="rounded-2xl border border-slate-200 bg-white p-6 shadow-2xs max-w-2xl space-y-4"
          >
            <div>
              <h2 className="text-base font-bold text-slate-900">
                Company Billing &amp; Trial Configuration
              </h2>
              <p className="text-xs text-slate-500">
                Manage billing and trial settings for company `{companyId}`.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  License Price (USD)
                </label>
                <input
                  type="text"
                  value={licensePriceDraft}
                  onChange={(e) => setLicensePriceDraft(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Package Price (USD)
                </label>
                <input
                  type="text"
                  value={packagePriceDraft}
                  onChange={(e) => setPackagePriceDraft(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Trial Days
                </label>
                <input
                  type="number"
                  min={0}
                  max={365}
                  value={trialDaysDraft}
                  onChange={(e) => setTrialDaysDraft(Number(e.target.value))}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Zoho Subscriptions Customer ID
                </label>
                <input
                  type="text"
                  value={zohoIdDraft}
                  onChange={(e) => setZohoIdDraft(e.target.value)}
                  placeholder="zoho_cust_..."
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-xs font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Stripe Customer ID
                </label>
                <input
                  type="text"
                  value={stripeIdDraft}
                  onChange={(e) => setStripeIdDraft(e.target.value)}
                  placeholder="cus_..."
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-xs font-mono"
                />
              </div>
            </div>

            <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <label className="flex items-center gap-2 text-xs font-medium text-slate-800">
                <input
                  type="checkbox"
                  checked={enableBillingDraft}
                  onChange={(e) => setEnableBillingDraft(e.target.checked)}
                />
                <span>Enable Billing Enforcement</span>
              </label>
              <label className="flex items-center gap-2 text-xs font-medium text-slate-800">
                <input
                  type="checkbox"
                  checked={cardExistsDraft}
                  onChange={(e) => setCardExistsDraft(e.target.checked)}
                />
                <span>Credit Card on File Verified</span>
              </label>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={savingBilling}
                className="rounded-xl bg-indigo-600 px-5 py-2.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {savingBilling ? 'Saving…' : 'Save Billing Settings'}
              </button>
            </div>
          </form>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white shadow-2xs overflow-hidden">
            <div className="table-scroll-container">
              <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
                <thead className="bg-slate-50 text-slate-600 uppercase font-semibold">
                  <tr>
                    <th className="px-4 py-3">Invoice #</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Total</th>
                    <th className="px-4 py-3">Balance Due</th>
                    <th className="px-4 py-3">Issued</th>
                    <th className="px-4 py-3 text-right">Settlement</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {companyInvoicesPagination.pageItems.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                        No invoices found for this company.
                      </td>
                    </tr>
                  ) : (
                    companyInvoicesPagination.pageItems.map((inv) => (
                      <tr key={inv.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-mono font-semibold">{inv.invoice_number}</td>
                        <td className="px-4 py-3">
                          <Badge variant={inv.status === 'paid' ? 'success' : 'warning'} dot>
                            {inv.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 font-mono">
                          {inv.currency} {inv.total}
                        </td>
                        <td className="px-4 py-3 font-mono">
                          {inv.currency} {inv.balance_due}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{formatDateTime(inv.issued_at)}</td>
                        <td className="px-4 py-3 text-right">
                          {inv.status !== 'paid' ? (
                            <button
                              type="button"
                              onClick={() => handleMarkInvoicePaid(inv)}
                              className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                            >
                              Mark as Paid
                            </button>
                          ) : (
                            <span className="text-emerald-700 font-medium">
                              Paid {formatDateTime(inv.paid_at)}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <PaginationBar pagination={companyInvoicesPagination} itemLabel="invoices" compact />
          </div>
        )}
      </div>
    );
  }

  // --------------------------------------------------------------------------
  // Render Main Companies Data Table (`/admin/companies`)
  // --------------------------------------------------------------------------
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Companies &amp; Tenant Billing ({companiesQuery.totalItems})
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Inspect customer Procore companies, manage trial/billing settings, and settle invoices.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => void companiesQuery.reload()}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <IconSync className="w-4 h-4" />
            <span>Refresh</span>
          </button>

          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700"
          >
            <IconPlus className="w-4 h-4" />
            <span>Register Company</span>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            companiesQuery.setPage(0);
          }}
          placeholder="Search by company name or exact company ID (UUID, Procore ID, or Legacy ID)…"
          className="w-full rounded-xl border border-slate-300 px-3.5 py-2 text-sm"
        />
      </div>

      {/* Companies Table */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-2xs overflow-hidden">
        <div className="table-scroll-container">
          <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
            <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider font-semibold">
              <tr>
                <th className="px-5 py-3.5">Company Name</th>
                <th className="px-4 py-3.5">Procore Company ID</th>
                <th className="px-4 py-3.5">Primary Billing Receiver</th>
                <th className="px-4 py-3.5">Extra CC Receivers</th>
                <th className="px-4 py-3.5">Created</th>
                <th className="px-5 py-3.5 text-right">Manage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {isSearchingCompanies ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                    Loading companies data table…
                  </td>
                </tr>
              ) : filteredCompanies.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                    No companies matched your filter.
                  </td>
                </tr>
              ) : (
                filteredCompanies.map((comp) => (
                  <tr key={comp.id} className="hover:bg-slate-50">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2.5">
                        <IconBuilding className="w-4 h-4 text-indigo-600 shrink-0" />
                        <div>
                          <Link
                            to={`/admin/companies/${comp.id}/users`}
                            className="text-sm font-semibold text-indigo-600 hover:underline"
                          >
                            {comp.name}
                          </Link>
                          <div className="font-mono text-[11px] text-slate-400">{comp.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 font-mono font-semibold text-slate-800">
                      #{comp.procore_company_id}
                    </td>
                    <td className="px-4 py-4 font-mono text-slate-600">
                      {comp.pbr_user_id ? `${comp.pbr_user_id.slice(0, 8)}…` : 'Not assigned'}
                    </td>
                    <td className="px-4 py-4 text-slate-600">
                      {comp.extra_billing_receivers?.length || 0} email(s)
                    </td>
                    <td className="px-4 py-4 text-slate-600">{formatDateTime(comp.created_at)}</td>
                    <td className="px-5 py-4 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        <Link
                          to={`/admin/companies/${comp.id}/users`}
                          className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                        >
                          Users
                        </Link>
                        <Link
                          to={`/admin/companies/${comp.id}/settings`}
                          className="rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                        >
                          Billing Settings
                        </Link>
                        <Link
                          to={`/admin/companies/${comp.id}/invoices`}
                          className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                        >
                          Invoices
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <PaginationBar pagination={companiesQuery} itemLabel="companies" />
      </div>

      {/* Create Company Dialog */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Register New Customer Company"
        subtitle="Create a new customer company record."
      >
        <form onSubmit={handleCreateCompany} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Company Name <span className="text-rose-600">*</span>
            </label>
            <input
              type="text"
              required
              value={newCompanyName}
              onChange={(e) => setNewCompanyName(e.target.value)}
              placeholder="Turner & Sons Builders"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Procore Company ID <span className="text-rose-600">*</span>
            </label>
            <input
              type="number"
              required
              min={1}
              value={newProcoreId}
              onChange={(e) => setNewProcoreId(e.target.value)}
              placeholder="4910293"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setCreateOpen(false)}
              className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-medium text-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creatingCompany}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {creatingCompany ? 'Creating…' : 'Create Company'}
            </button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
